import { useState } from 'react'
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from "recharts"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { BarChart3, Info } from 'lucide-react'
import type { LibroStats } from '../types'

interface DistribucionCodigosChartProps {
  stats: LibroStats
}

const chartConfig = {
  cantidad: {
    label: "Códigos",
    color: "#14b8a6",
  },
} satisfies ChartConfig

const BAR_COLORS = {
  Canjeados: '#14b8a6',
  Disponibles: '#0d9488',
  Inactivos: '#9ca3af',
}

export function DistribucionCodigosChart({ stats }: DistribucionCodigosChartProps) {
  const [showInfo, setShowInfo] = useState(false)

  const inactivos = stats.total_codigos - stats.codigos_canjeados - stats.codigos_disponibles

  const chartData = [
    { categoria: 'Canjeados', cantidad: stats.codigos_canjeados },
    { categoria: 'Disponibles', cantidad: stats.codigos_disponibles },
    { categoria: 'Inactivos', cantidad: Math.max(0, inactivos) },
  ]

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center">
          <CardTitle>Distribución de Códigos</CardTitle>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-4 w-4" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Canjeados, disponibles e inactivos
            </span>
          )}
        </div>
        <BarChart3 className="h-4 w-4 text-teal-500" />
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[250px] w-full">
          <BarChart
            accessibilityLayer
            data={chartData}
            layout="vertical"
            margin={{
              left: 0,
              right: 50,
            }}
          >
            <XAxis type="number" dataKey="cantidad" hide />
            <YAxis
              dataKey="categoria"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              width={100}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  hideLabel
                  formatter={(value, _name, item) => {
                    const total = stats.total_codigos
                    const pct = total > 0 ? ((Number(value) / total) * 100).toFixed(1) : '0'
                    return `${item.payload.categoria}: ${value} (${pct}%)`
                  }}
                />
              }
            />
            <Bar dataKey="cantidad" radius={4}>
              {chartData.map((entry, index) => (
                <Cell key={index} fill={BAR_COLORS[entry.categoria as keyof typeof BAR_COLORS]} />
              ))}
              <LabelList
                dataKey="cantidad"
                position="right"
                offset={8}
                className="fill-foreground"
                fontSize={12}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
